import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Plus, Briefcase } from "lucide-react";
import { toast } from "sonner";

const CRMDeals = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "", value: "", status: "pending", notes: ""
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchDeals();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/crm/deals`, {
        withCredentials: true
      });
      setDeals(response.data || []);
    } catch (error) {
      console.error("Error fetching deals:", error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/deals`, {
        ...formData,
        value: parseFloat(formData.value)
      }, { withCredentials: true });
      toast.success("Deal created!");
      setDialogOpen(false);
      setFormData({ title: "", value: "", status: "pending", notes: "" });
      fetchDeals();
    } catch (error) {
      toast.error("Failed to create deal");
    }
  };

  const updateStatus = async (dealId, status) => {
    try {
      await axios.put(`${API}/crm/deals/${dealId}?status=${status}`, {}, {
        withCredentials: true
      });
      fetchDeals();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    pending: "bg-gray-100 text-gray-700",
    in_progress: "bg-blue-100 text-blue-700",
    closed_won: "bg-green-100 text-green-700",
    closed_lost: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif">Deals</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-deal-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Deal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Deal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Property Sale - 123 Main St"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value (K) *</label>
                <Input
                  required
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="500000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed_won">Closed Won</SelectItem>
                    <SelectItem value="closed_lost">Closed Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full">Create Deal</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-sm p-4 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-6 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : deals.length === 0 ? (
        <div className="text-center py-16 bg-muted rounded-sm">
          <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif text-xl mb-2">No Deals Yet</h3>
          <p className="text-muted-foreground">Create your first deal to start tracking revenue.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deals.map((deal) => (
            <div key={deal.deal_id} className="bg-card border border-border rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium">{deal.title}</h3>
                  <p className="font-mono text-xl font-semibold text-primary mt-1">
                    K{deal.value.toLocaleString()}
                  </p>
                </div>
                <Select value={deal.status} onValueChange={(v) => updateStatus(deal.deal_id, v)}>
                  <SelectTrigger className={`w-32 h-8 text-xs ${statusColors[deal.status]}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed_won">Closed Won</SelectItem>
                    <SelectItem value="closed_lost">Closed Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {deal.notes && (
                <p className="mt-3 text-sm text-muted-foreground">{deal.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CRMDeals;
